import { m } from "@nerves/i18n/messages";
import { CheckCircle, Info, WarningCircle, X } from "@phosphor-icons/react";
import {
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastRegion as AriaToastRegion,
  Button,
  Text,
  UNSTABLE_ToastContent as ToastContent,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import { type ToastContent as Content, toastQueue } from "@/lib/toast";

const icons = { info: Info, success: CheckCircle, danger: WarningCircle } as const;
const iconTone = { info: "text-accent-text", success: "text-success-text", danger: "text-danger-text" } as const;

/**
 * Mount once at the root. React Aria gives it a region landmark (F6 jumps to it), aria-live
 * announcements, timer pause on hover/focus and Escape-to-dismiss.
 */
export function ToastRegion() {
  return (
    <AriaToastRegion
      queue={toastQueue}
      aria-label={m.toast_region()}
      data-toast-region
      className="fixed right-4 bottom-4 z-[70] flex w-[min(384px,calc(100vw-32px))] flex-col gap-2 outline-none max-md:bottom-[calc(var(--bottombar-h)+12px)]"
    >
      {({ toast }) => {
        const c: Content = toast.content;
        const tone = c.tone ?? "info";
        const I = icons[tone];
        return (
          <AriaToast
            toast={toast}
            className="anim-toast flex items-start gap-2.5 rounded-panel border border-popover-border bg-raised p-3 text-sm text-fg shadow-lg outline-none"
          >
            <I size={16} weight="fill" aria-hidden="true" className={cx("mt-0.5 shrink-0", iconTone[tone])} />
            <ToastContent className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Text slot="title" className="font-medium">
                {c.href ? (
                  <a
                    href={c.href}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                      e.preventDefault();
                      // The router lives below the toast region; the app layout listens for this.
                      window.dispatchEvent(new CustomEvent("pt:navigate", { detail: c.href }));
                      toastQueue.close(toast.key);
                    }}
                    className="rounded-mark underline decoration-line-strong underline-offset-2 outline-hidden hover:decoration-fg focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
                  >
                    {c.title}
                  </a>
                ) : (
                  c.title
                )}
              </Text>
              {c.description ? (
                <Text slot="description" className="text-xs text-fg-muted">
                  {c.description}
                </Text>
              ) : null}
            </ToastContent>
            {c.action ? (
              <Button
                onPress={() => {
                  c.action?.onAction();
                  toastQueue.close(toast.key);
                }}
                className="h-(--control-h-sm) shrink-0 rounded-control px-2 text-xs font-semibold text-accent-text data-hovered:bg-accent-soft data-pressed:bg-accent-soft-hover"
              >
                {c.action.label}
              </Button>
            ) : null}
            <Button
              slot="close"
              aria-label={m.toast_dismiss()}
              className="inline-flex size-(--control-h-sm) shrink-0 items-center justify-center rounded-control text-fg-muted data-hovered:bg-hover data-hovered:text-fg data-pressed:bg-active"
            >
              <X size={14} aria-hidden="true" />
            </Button>
          </AriaToast>
        );
      }}
    </AriaToastRegion>
  );
}
